// MT-11C.2 — pure, framework-free clinic branding state (node-testable).
// The CLINIC object is intentionally MUTATED in place (never re-assigned) so
// every importer reads live values on each render. Nothing clinic-specific is
// hardcoded here: these are neutral Prescribify fallbacks, overwritten from the
// ACTIVE tenant's saved profile via applyClinicPayload().
export const CLINIC_DEFAULTS = Object.freeze({
  configured: true, // server-confirmed; set false only when the API reports an unconfigured instance
  name: "Prescribify Clinic",
  short: "Prescribify",
  address: "",
  phones: [],
  email: "",
  website: "",
  doctorName: "",
  qualifications: "",
  regNo: "",
  specialties: "",
  timings: "",
  doctor_photo_url: "",
  services_whatsapp: "",
  footer: "",
  logo_url: "",
  brand_color: "#DB2777",
  print_header_style: "detailed",   // "detailed" | "compact" | "logo-only"
  doctors: [],                       // [{id, name, qualifications, regNo, signature_url}]
  // "My Choice" brand tier — ordered list of the doctor's preferred pharma
  // companies. First-available brand for each molecule wins.
  preferred_companies: [
    "Eris", "Lupin", "Mankind", "Glenmark", "Dr Reddy's",
    "Cipla", "GDP Life Sciences", "Renwik", "Macleods",
  ],
});

const clone = (v) => (typeof structuredClone === "function" ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

export const CLINIC = clone(CLINIC_DEFAULTS);

export const CLINIC_UPDATED_EVENT = "clinic-profile-updated";

// Browser/PDF document title — "<clinic> — <part> — …" from the ACTIVE tenant
// profile plus optional server-derived parts (e.g. the signed prescriber).
export function documentTitleFor(...parts) {
  return [CLINIC.name, ...parts].map((p) => (p || "").toString().trim()).filter(Boolean).join(" — ");
}

// Notify listeners (LogoTile, Layout header, Login) that the profile changed
// and keep the document title (= PDF Title metadata) on the active tenant.
export function notifyClinicUpdated() {
  try {
    if (typeof document !== "undefined") document.title = documentTitleFor();
  } catch { /* noop */ }
  try {
    if (typeof window !== "undefined" && typeof Event === "function") window.dispatchEvent(new Event(CLINIC_UPDATED_EVENT));
  } catch { /* noop */ }
}

// Mutator — overwrites CLINIC in place. Skips undefined/null values so the
// backend can send a partial patch without wiping unrelated fields.
export function setClinic(next) {
  if (!next || typeof next !== "object") return;
  Object.keys(next).forEach((k) => {
    if (next[k] !== undefined && next[k] !== null) CLINIC[k] = next[k];
  });
  notifyClinicUpdated();
}

// Restore the neutral defaults IN PLACE (same object identity) and drop any
// tenant-specific extra keys — nothing from a previous tenant may survive.
export function resetClinic({ silent = false } = {}) {
  Object.keys(CLINIC).forEach((k) => { delete CLINIC[k]; });
  Object.assign(CLINIC, clone(CLINIC_DEFAULTS));
  if (!silent) notifyClinicUpdated();
  return CLINIC;
}

// Replace the whole branding state with ONE /settings/clinic response for the
// active tenant. A missing/failed payload leaves the neutral defaults so a
// previous tenant's branding can never leak through a failed refetch.
export function applyClinicPayload(data) {
  resetClinic({ silent: true });
  if (data && typeof data === "object" && data.clinic) setClinic(data.clinic);
  CLINIC.configured = data ? data.configured !== false : true;
  notifyClinicUpdated();
  return CLINIC;
}
